import { ApolloProvider } from '@apollo/client/react';
import { inject as injectAnalytics } from '@vercel/analytics';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Route, Routes, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { apolloClient } from './apollo';
import { ProtectedLayout } from './auth/ProtectedLayout';
import { PublicLayout } from './auth/PublicLayout';
import { AdminCenter } from './pages/admin-center';
import { FailedUploads } from './pages/admin-center/FailedUploads';
import { Invoices } from './pages/admin-center/Invoices';
import { DebugTools } from './pages/admin-center/DebugTools';
import { ForgotPassword } from './pages/authentication/ForgotPassword';
import { Invitation } from './pages/authentication/Invitation';
import { Login } from './pages/authentication/Login';
import { Logout } from './pages/authentication/Logout';
import { Register } from './pages/authentication/Register';
import { ResetPassword } from './pages/authentication/ResetPassword';
import { Organisations } from './pages/organisations';
import { CompanyInfo } from './pages/organisation/CompanyInfo';
import { Subscription } from './pages/organisation/Subscription';
import { CustomerCenter } from './pages/organisation/CustomerCenter';
import { Sales } from './pages/organisation/Sales';
import { Referrals } from './pages/organisation/Referrals';
import { ObjectLibrary } from './pages/organisation/ObjectLibrary';
import { ObjectEditor } from './pages/objectEditor/ObjectEditor';
import { TemplateLibrary } from './pages/organisation/TemplateLibrary';

import PotreeRenderer from './pages/potree-renderer';
import { Projects } from './pages/projects';
import { Project } from './pages/projects/Project';
import { Users } from './pages/users';
import { Profile } from './pages/users/Profile';
import store from './redux/store';
import { Language, Permissions } from './types/graphqlTypes';
import { ErrorBoundary } from './pages/error-boundary';
import { InvalidProject } from './pages/projects/invalidProject';
import { NoOrganisationFound } from './pages/organisations/NoOrganisationFound';
import { LanguageProvider } from './translation/src';
import { lazy, Suspense, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { EarthLoader } from './components/loaders/EarthLoader';

import EN from '../translations/en.json';
import DE from '../translations/de.json';
import NL from '../translations/nl.json';
import FR from '../translations/fr.json';
import JA from '../translations/ja.json';
import PT from '../translations/pt.json';
import App from './app/app';
import { HintBarProvider } from './hooks/useHintBar';
import { useUserEvents } from './hooks/modules/user/useUserEvents';
import { ToastNotificationsProvider } from './hooks/useToastNotifications';
import { OrderConfirmationProvider } from './modules/organisation/OrderConfirmationModal';
import { LivechatLayout } from './modules/organisation/LivechatLayout';
import { ApiKeys } from './pages/api/ApiKeys';
import { SharedProjectApp, SharedProjectRedirect } from './pages/share/SharedProject';

// The Template Editor brings the print engine (pdf-lib, fontkit): loaded when opened.
const TemplateEditor = lazy(() => import('./pages/templateEditor/TemplateEditor'));

const Home = () => {
  return <div />;
};

const UserEvents: React.FC = () => {
  useUserEvents();
  return null;
};

/**
 * Applies a `?lang=` URL parameter to the app language, then strips it.
 *
 * Declared at module scope: defining it inside `AppContainer` made it a new component type on
 * every render, which remounts it (and discards its effect state) each time.
 */
const LanguageURLSync: React.FC<{ language: Language; setLanguage: (l: Language) => void }> = ({ language, setLanguage }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const langParamURL = searchParams.get('lang')?.toUpperCase() as Language | undefined;

  useEffect(() => {
    // Check if langParamURL is a valid Language enum value
    const validLanguageURL = langParamURL && Object.values(Language).includes(langParamURL) ? langParamURL : undefined;

    if (validLanguageURL && validLanguageURL !== language) {
      setLanguage(validLanguageURL);
    }

    // Param is handled, clean up to avoid update loop
    if (langParamURL) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('lang');
      setSearchParams(newParams, { replace: true });
    }
  }, [langParamURL, searchParams, setSearchParams, language, setLanguage]);

  return null;
};

export const AppContainer: React.FC = () => {
  useEffect(() => {
    if (import.meta.env.MODE !== 'local') injectAnalytics();
  }, []);
  // Persisted so a choice made before signing in survives reloads and redirects.
  const [language, setLanguage] = useLocalStorage<Language>('language', Language.En);

  return (
    <ReduxProvider store={store}>
      <ApolloProvider client={apolloClient}>
        <LanguageProvider language={language} languages={{ EN, NL, DE, FR, JA, PT }} setLanguage={setLanguage}>
          <HintBarProvider>
            <ToastNotificationsProvider>
              <ErrorBoundary>
                <BrowserRouter>
                  <LanguageURLSync language={language} setLanguage={setLanguage} />
                  <UserEvents />
                  <OrderConfirmationProvider>
                    <Routes>
                      <Route path="/logout" element={<Logout />} />
                      <Route element={<PublicLayout />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/register-invite" element={<Invitation />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                      </Route>
                      {/* "Anyone with the link" viewing: outside the login-walled <App/>, read-only, one project. */}
                      <Route path="/share/:token" element={<SharedProjectApp />}>
                        <Route index element={<SharedProjectRedirect />} />
                        <Route path="organisations/:organisationId/projects/:projectId" element={<Project />} />
                      </Route>
                      <Route path="/" element={<App />}>
                        <Route index element={<Home />} />
                        <Route path="potree-renderer" element={<PotreeRenderer />} />
                        <Route path="organisations" element={<Organisations />}>
                          <Route path=":organisationId" element={<LivechatLayout />}>
                            <Route
                              index
                              element={
                                <ProtectedLayout permissions={[Permissions.Projects, Permissions.ProjectsRead]}>
                                  <Projects />
                                </ProtectedLayout>
                              }
                            />
                            {/* A partner's admin may list a referred organisation's projects, not open one. */}
                            <Route path="projects">
                              <Route
                                index
                                element={
                                  <ProtectedLayout permissions={[Permissions.Projects, Permissions.ProjectsRead, Permissions.Partner]}>
                                    <Projects />
                                  </ProtectedLayout>
                                }
                              />
                              <Route
                                path=":projectId"
                                element={
                                  <ProtectedLayout permissions={[Permissions.Projects, Permissions.ProjectsRead]}>
                                    <Project />
                                  </ProtectedLayout>
                                }
                              />
                            </Route>
                            <Route path="invalid-project">
                              <Route index element={<InvalidProject />} />
                            </Route>
                            <Route path="users" element={<ProtectedLayout permissions={[Permissions.Users]} />}>
                              <Route index element={<Users />} />
                            </Route>
                            <Route path="api-keys">
                              <Route index element={<ApiKeys />} />
                            </Route>
                            <Route path="company-info" element={<ProtectedLayout permissions={[Permissions.Billing]} />}>
                              <Route index element={<CompanyInfo />} />
                            </Route>
                            <Route path="subscription" element={<ProtectedLayout permissions={[Permissions.Billing]} />}>
                              <Route index element={<Subscription />} />
                            </Route>
                            <Route path="object-library" element={<ProtectedLayout permissions={[Permissions.ObjectLibrary]} />}>
                              <Route index element={<ObjectLibrary />} />
                              <Route path=":definitionId/edit" element={<ObjectEditor />} />
                            </Route>
                            <Route path="templates" element={<ProtectedLayout permissions={[Permissions.TemplateLibrary]} />}>
                              <Route index element={<TemplateLibrary />} />
                              <Route
                                path=":templateId/edit"
                                element={
                                  <Suspense fallback={<EarthLoader />}>
                                    <TemplateEditor />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route path="referrals" element={<ProtectedLayout permissions={[Permissions.Billing]} />}>
                              <Route index element={<Referrals />} />
                              <Route path="customers" element={<CustomerCenter />} />
                              <Route path="sales" element={<Sales />} />
                            </Route>
                          </Route>
                        </Route>
                        <Route path="no-organisation">
                          <Route index element={<NoOrganisationFound />} />
                        </Route>
                        <Route path="me">
                          <Route path="profile" element={<Profile />} />
                        </Route>
                        <Route path="admin-center">
                          <Route index element={<AdminCenter />} />
                          <Route path="failed-uploads" element={<FailedUploads />} />
                          <Route path="invoices" element={<Invoices />} />
                          <Route path="debug" element={<DebugTools />} />
                        </Route>
                      </Route>
                    </Routes>
                  </OrderConfirmationProvider>
                </BrowserRouter>
              </ErrorBoundary>
              <ToastContainer />
            </ToastNotificationsProvider>
          </HintBarProvider>
        </LanguageProvider>
      </ApolloProvider>
    </ReduxProvider>
  );
};
